import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { Scene, sortScenes } from '@lib/data/story';
import {
  anchorEnd,
  anchorStart,
  assignSpanLanes,
  COMMENT_SPAN_LANES,
  formatAnchorLabel,
} from '@lib/media/commentAnchor';
import { authorLabel } from '@lib/media/commentAuthor';
import type { OriginalUnavailableReason } from '@lib/media/originalMedia';
import { previewResolutionLabel, resolutionLabel } from '@lib/media/resolutionLabel';
import { showToast } from '@lib/utils/toastmaster';
import { MomentMergeVideoPlayer } from '@pages/Search/components/MomentGrid/components/MomentMerge/components/MomentMergeVideoPlayer/MomentMergeVideoPlayer';
import {
  ChapterMark,
  CommentMark,
  MomentMergeVideoPlayerRef,
  PlaybackSource,
  SelectionSpan,
} from '@pages/Search/components/MomentGrid/components/MomentMerge/components/MomentMergeVideoPlayer/types';
import type { MediaComment } from '@stores/MediaComments';
import {
  BeginEndTimestamp,
  Clip,
  ExtendedMediaId,
  MomentMergeStore,
} from '@stores/MomentMerge/MomentMergeStore';
import { Renderer } from '@stores/Renderer/Renderer';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { hasSpan, percentOfClip, secondsAtPercent } from './clipGeometry';
import css from './VideoPlayer.module.scss';
import { VideoScanningOverlay } from './VideoScanningOverlay';

/**
 * The absolute seconds the player's single clip spans: the trimmed moment in `moment` mode,
 * the whole media in `full`.
 *
 * One expression, three readers below — the clip the store builds, the effect that rebuilds
 * it, and the scene ticks drawn against it. `scrubTo` clamps a seek to this range, so ticks
 * placed against a differently-derived range would point at frames a click cannot reach.
 */
const clipRange = (
  mode: 'moment' | 'full',
  momentStart: number | undefined,
  momentEnd: number | undefined,
  durationSeconds: number,
) => ({
  start: mode === 'moment' && momentStart !== undefined ? momentStart : 0,
  end: mode === 'moment' && momentEnd !== undefined ? momentEnd : durationSeconds,
});

/**
 * How long to wait after a mode change before scrubbing.
 *
 * The clip is rebuilt in an effect keyed on the mode, so a scrub in the same tick clamps
 * against the clip that is about to be replaced. Every site that seeks across a mode change
 * reads this constant, so grepping it finds them all: `handleModeChange` below, and
 * `scrubAfterClipRebuild` in `MomentModal` (which serves both the Scenes panel and the mobile
 * Moment / Full toggle).
 *
 * A timed hop rather than an exact one, and it can go: `VideoPlayer` is a child of
 * `MomentModal`, so a parent effect keyed on the mode runs after this child's clip rebuild.
 * That route depends on the parent re-rendering on the mode change, which it only does while
 * it observes `mode`. When it lands, all readers of this constant go together.
 */
export const CLIP_REBUILD_DELAY_MS = 10;

type VideoPlayerBaseProps = {
  media: Media;
  onReady?: () => void;
  hidden?: boolean;
  containerClassName?: string;
  withModeToggle?: boolean;
  toggleClassName?: string;
  showScanningEffect?: boolean;
  /**
   * Comment anchors to mark on the scrub bar, in absolute media seconds. Converted to the
   * bar's own percentages here for the same reason the scenes are — see `chapters` below.
   */
  comments?: MediaComment[];
  /** The marked comment, drawn stronger than the rest. */
  selectedCommentId?: string | null;
  /** A marker was pressed. Without it the marks are drawn but inert. */
  onSelectComment?: (commentId: string) => void;
  /** Scenes to mark on the scrub bar, in absolute media seconds. */
  scenes?: Scene[];
};

/**
 * `selectableRange` and `store` are tied together at the type level, not just by convention:
 * marking a range writes `isDragging` (see `handleSelectionDragChange`) to whatever `playerStore`
 * resolves to, and without an explicit `store` that falls through to the app-wide singleton
 * exported from `@stores/VideoPlayer`. A call site that fell through would latch that flag on
 * the singleton for the rest of the session if a drag ever ended through a path the release
 * plumbing did not anticipate — see `.claude/docs/moment-range-selection.md`. Requiring `store`
 * here makes that unreachable instead of merely undocumented; the fallback stays available for
 * hosts like `BaseMomentModal` that never set `selectableRange`.
 */
type VideoPlayerProps = VideoPlayerBaseProps &
  (
    | {
        /**
         * Lets the viewer mark a range on the scrub bar, writing it to `store.moment`.
         *
         * Off by default, because two of this component's hosts have no use for it: the merge
         * editor's bar spans several clips, and `BaseMomentModal` shows a fixed moment with no
         * action to hand the range to.
         */
        selectableRange: true;
        store: VideoPlayerStore;
      }
    | {
        selectableRange?: false;
        store?: VideoPlayerStore;
      }
  );

export type VideoPlayerRef = {
  scrubTo: (time: number) => void;
  video: HTMLVideoElement | null;
};

export const VideoPlayer = observer(
  forwardRef<VideoPlayerRef, VideoPlayerProps>((props: VideoPlayerProps, ref) => {
    const {
      media,
      onReady,
      hidden,
      containerClassName,
      withModeToggle,
      toggleClassName,
      store,
      showScanningEffect,
      scenes,
      comments,
      selectedCommentId,
      onSelectComment,
      selectableRange,
    } = props;

    // Fall back to the global store. Only reachable when `selectableRange` is unset — see the
    // note on `VideoPlayerProps`.
    const playerStore = store || videoPlayerStore;

    const startTime = playerStore.moment?.start;
    const endTime = playerStore.moment?.end;
    const durationSeconds = media.metadata?.video?.duration ?? 0;

    /**
     * What the player is actually playing, in absolute media seconds.
     *
     * Derived once and depended on as two numbers, rather than each consumer calling
     * `clipRange` and depending on `mode`/`moment` — which is load-bearing, not tidying. The
     * marked range is written on every frame of a drag, so a memo keyed on `moment` is a memo
     * that misses on every frame. Two of them below build a whole `MomentMergeStore` and
     * `Renderer`, so in `full` mode — where the clip is the whole media no matter where the
     * range sits — dragging a range would have torn the player down and rebuilt it sixty times
     * a second. Keyed on the bounds, that drag changes nothing and rebuilds nothing.
     *
     * In `moment` mode the bounds really do move with the range, so trimming still rebuilds, as
     * it always has. That is why marking is a `full`-mode job.
     */
    const { start: clipStart, end: clipEnd } = clipRange(
      playerStore.mode,
      startTime,
      endTime,
      durationSeconds,
    );

    const momentMergeVideoPlayerRef = useRef<MomentMergeVideoPlayerRef>(null);

    /**
     * Scene divisions for the scrub bar, converted from absolute media seconds into fractions
     * of the bar.
     *
     * The conversion belongs here rather than in the player: the bar shows the *clip*, whose
     * bounds come from the same `clipRange` the clip store is built from. In
     * `moment` mode that clip is the trimmed range, so a scene before or after it maps outside
     * 0–100 — kept rather than clamped, so a scene the moment lands in the middle of still
     * resolves a hover, and the bar clips the tick itself.
     */
    const chapters = useMemo<ChapterMark[]>(() => {
      if (!scenes?.length) {
        return [];
      }
      const clip = { start: clipStart, end: clipEnd };
      if (!hasSpan(clip)) {
        return [];
      }
      return sortScenes(scenes, durationSeconds)
        .map((scene) => ({
          startPercent: percentOfClip(scene.start ?? 0, clip),
          endPercent: percentOfClip(scene.end ?? scene.start ?? 0, clip),
          title: scene.title ?? `Scene ${scene.scene_id}`,
        }))
        .filter((chapter) => chapter.endPercent > 0 && chapter.startPercent < 100);
    }, [scenes, durationSeconds, clipStart, clipEnd]);

    /**
     * Comment anchors as spans of the bar, against the same clip the chapters use — so a note
     * and the scene it sits in stay lined up in `moment` mode, where the bar shows only the
     * trimmed range.
     *
     * Filtered inclusively at both ends, unlike the chapters above: a chapter starting at 0
     * needs no tick because the bar's own edge is the division, but a note left on the first
     * frame is a note, and dropping it would make it unreachable from the bar.
     */
    const currentUserId = userStore.data?._id ?? null;
    const commentMarks = useMemo<CommentMark[]>(() => {
      if (!comments?.length) {
        return [];
      }
      const clip = { start: clipStart, end: clipEnd };
      if (!hasSpan(clip)) {
        return [];
      }
      // Rows are assigned over every comment, not just the ones this clip shows, so a span keeps
      // its row as the reader switches between `moment` and `full` rather than hopping.
      const lanes = assignSpanLanes(comments, COMMENT_SPAN_LANES);
      return comments
        .map((comment) => ({
          id: comment.id,
          startPercent: percentOfClip(anchorStart(comment.anchor), clip),
          endPercent: percentOfClip(anchorEnd(comment.anchor), clip),
          lane: lanes.get(comment.id) ?? 0,
          preview: {
            author: authorLabel(comment.author, currentUserId),
            label: formatAnchorLabel(comment.anchor),
            text: comment.body,
          },
        }))
        .filter((mark) => mark.endPercent >= 0 && mark.startPercent <= 100);
    }, [comments, clipStart, clipEnd, currentUserId]);

    /**
     * The marked range, as a span of the bar — and only in `full` mode.
     *
     * In `moment` mode the bar already spans the range (`clipRange`), so the band would be the
     * whole bar and dragging an edge would rescale the very thing being dragged. The two modes
     * split the job instead: `full` is where a range is marked and adjusted, `moment` is where
     * it is played back. That is also why the Moment / Full toggle has to be reachable wherever
     * a range can be marked.
     */
    const selection = useMemo<SelectionSpan | undefined>(() => {
      if (playerStore.mode !== 'full') return undefined;
      if (startTime === undefined || endTime === undefined) return undefined;
      const clip = { start: 0, end: durationSeconds };
      if (!hasSpan(clip)) return undefined;
      return {
        startPercent: percentOfClip(startTime, clip),
        endPercent: percentOfClip(endTime, clip),
      };
    }, [playerStore.mode, startTime, endTime, durationSeconds]);

    /**
     * Whether the bar is in range-marking mode at all: the host asked for it, and the bar is
     * showing the whole media rather than the range itself (see the note on `selection`).
     *
     * Resolved once so the band's handles, the gesture that moves them and the drag signal
     * cannot end up gated differently — a bar that draws handles it will not move is worse than
     * one with none.
     */
    const rangeSelectable = !!selectableRange && playerStore.mode === 'full';

    const handleSelectionChange = useCallback(
      (next: SelectionSpan) => {
        // Always against the whole media, never `clipStart`/`clipEnd`: the band is only ever
        // drawn and dragged in `full` mode, where those are the same thing, and reading the
        // clip here would silently start measuring against the range being edited if that ever
        // changed.
        const clip = { start: 0, end: durationSeconds };
        if (!hasSpan(clip)) return;
        playerStore.setMoment({
          start: secondsAtPercent(next.startPercent, clip),
          end: secondsAtPercent(next.endPercent, clip),
        });
      },
      [playerStore, durationSeconds],
    );

    /**
     * Marking a range moves the same `moment` the filmstrip's trim head does, so it has to
     * declare itself the same way: `isDragging` is what tells the filmstrip to stop generating
     * frames while the decoder and network are busy (see `VirtualizedVideoTimeline`'s `isBusy`).
     * Without it the strip kept capturing through a gesture that rewrites the moment on every
     * frame, which is the most expensive moment there is to ask it for a frame.
     */
    const handleSelectionDragChange = useCallback(
      (dragging: boolean) => {
        playerStore.setIsDragging('scrub-bar', dragging);
      },
      [playerStore],
    );

    // The viewer's quality pick, for this media in this modal only. Deliberately not persisted:
    // the preview is the right default on every new piece of media, and an original chosen once
    // on a fast connection should not decide how the next 4K upload starts playing.
    const [playbackSource, setPlaybackSource] = useState<PlaybackSource>('preview');

    useEffect(() => {
      setPlaybackSource('preview');
    }, [media._id]);

    // What each quality row advertises. The original's comes from the API; the preview's is the
    // transcode ladder's, clamped to the source (see `previewResolutionLabel`).
    const playbackSourceLabels = useMemo(
      () => ({
        preview: previewResolutionLabel(
          media.metadata?.video?.width,
          media.metadata?.video?.height,
        ),
        original:
          resolutionLabel(media.metadata?.video?.width, media.metadata?.video?.height) ?? undefined,
      }),
      [media.metadata?.video?.width, media.metadata?.video?.height],
    );

    const handleOriginalUnavailable = useCallback(
      (reason: OriginalUnavailableReason) => {
        if (playbackSource !== 'original') return;
        setPlaybackSource('preview');
        showToast('media-original-playback-failed', { reason });
      },
      [playbackSource],
    );

    // Create a MomentMergeStore with a single clip
    const clipStore = useMemo(() => {
      const renderer = new Renderer();
      const store = new MomentMergeStore(renderer);

      const extendedMediaId = new ExtendedMediaId(media._id, media.libraryId);

      // Create clip with absolute times (like MomentSelection does)
      const beginEndTimestamp = new BeginEndTimestamp(clipStart, clipEnd);
      const clip = new Clip(`${media._id}-clip`, extendedMediaId, beginEndTimestamp);

      store.setClips([clip]);

      return store;
      // Keyed on the clip's own bounds, not on `mode`/`moment` — see the note on `clipStart`.
      // This builds a store and a renderer, so a dependency that changes per drag frame would
      // build one per frame.
    }, [media._id, media.libraryId, clipStart, clipEnd]);

    // Update clip when the played range changes
    useEffect(() => {
      const extendedMediaId = new ExtendedMediaId(media._id, media.libraryId);

      // Create clip with absolute times (like MomentSelection does)
      const beginEndTimestamp = new BeginEndTimestamp(clipStart, clipEnd);
      const clip = new Clip(`${media._id}-clip`, extendedMediaId, beginEndTimestamp);

      clipStore.setClips([clip]);
    }, [clipStore, clipStart, clipEnd, media._id, media.libraryId]);

    // Simple initial sync of video player store state with the clip store
    useEffect(() => {
      // Convert absolute time to virtual time for initial sync
      const currentClip = clipStore.clips[0];
      if (currentClip) {
        const absoluteTime = playerStore.currentTime;
        const clipStartTime = currentClip.startTime;
        const clipEndTime = currentClip.endTime;

        // Convert absolute time to virtual time (0-based within the clip)
        let virtualTime = 0;
        if (absoluteTime >= clipStartTime && absoluteTime <= clipEndTime) {
          virtualTime = absoluteTime - clipStartTime;
        } else if (absoluteTime > clipEndTime) {
          virtualTime = clipEndTime - clipStartTime; // End of clip
        }

        clipStore.setCurrentTime(virtualTime);
      } else {
        clipStore.setCurrentTime(0);
      }

      clipStore.setIsPlaying(playerStore.isPlaying);
      clipStore.setIsFullscreen(playerStore.isFullscreen);
      clipStore.setIsMuted(playerStore.isMuted);
      clipStore.setVolume(playerStore.volume);
      clipStore.setPrevVolume(playerStore.prevVolume);
      clipStore.setPreview(playerStore.preview);
    }, [clipStore]);

    // Sync playerStore absolute time changes to clipStore virtual time
    useEffect(() => {
      const currentClip = clipStore.clips[0];
      if (currentClip) {
        const absoluteTime = playerStore.currentTime;
        const clipStartTime = currentClip.startTime;
        const clipEndTime = currentClip.endTime;

        // Convert absolute time to virtual time (0-based within the clip)
        let virtualTime = 0;
        if (absoluteTime >= clipStartTime && absoluteTime <= clipEndTime) {
          virtualTime = absoluteTime - clipStartTime;
        } else if (absoluteTime > clipEndTime) {
          virtualTime = clipEndTime - clipStartTime; // End of clip
        }

        // Only update if there's a significant difference to avoid feedback loops
        if (Math.abs(clipStore.currentTime - virtualTime) > 0.1) {
          clipStore.setCurrentTime(virtualTime);
        }
      }
    }, [playerStore.currentTime, clipStore]);

    // Sync clipStore virtual time back to playerStore absolute time
    useEffect(() => {
      const currentClip = clipStore.clips[0];
      if (currentClip) {
        const absoluteTime = currentClip.startTime + clipStore.currentTime;
        if (Math.abs(playerStore.currentTime - absoluteTime) > 0.1) {
          playerStore.setCurrentTime(absoluteTime);
        }
      }
    }, [clipStore.currentTime, playerStore]);

    // Simple bidirectional sync between stores
    useEffect(() => {
      clipStore.setIsPlaying(playerStore.isPlaying);
    }, [playerStore.isPlaying, clipStore]);

    useEffect(() => {
      clipStore.setIsFullscreen(playerStore.isFullscreen);
    }, [playerStore.isFullscreen, clipStore]);

    useEffect(() => {
      clipStore.setIsMuted(playerStore.isMuted);
    }, [playerStore.isMuted, clipStore]);

    useEffect(() => {
      clipStore.setVolume(playerStore.volume);
    }, [playerStore.volume, clipStore]);

    // Sync clipStore changes back to playerStore
    useEffect(() => {
      playerStore.setIsPlaying(clipStore.isPlaying);
    }, [clipStore.isPlaying, playerStore]);

    // Leave the player store not-playing when this player goes away.
    //
    // `playerStore` is sometimes the app-wide singleton (BaseMomentModal passes
    // no store), and nothing else ever writes `isPlaying` back to false — the
    // clip store's own cleanup can't, because this mirroring effect is torn down
    // by then. Left unreset, the singleton stays `true` for the rest of the
    // session: it seeds the next modal's clip store as playing, and it would
    // permanently starve any work that pauses itself during playback.
    useEffect(() => {
      return () => {
        playerStore.setIsPlaying(false);
      };
    }, [playerStore]);

    useEffect(() => {
      playerStore.setIsFullscreen(clipStore.isFullscreen);
    }, [clipStore.isFullscreen, playerStore]);

    useEffect(() => {
      playerStore.setIsMuted(clipStore.isMuted);
    }, [clipStore.isMuted, playerStore]);

    useEffect(() => {
      playerStore.setVolume(clipStore.volume);
    }, [clipStore.volume, playerStore]);

    // Expose the same interface as the original VideoPlayer
    useImperativeHandle(ref, () => ({
      scrubTo: (time: number) => {
        if (momentMergeVideoPlayerRef.current) {
          // Update the original video player store
          playerStore.setCurrentTime(time);

          // Convert absolute time to virtual timeline time
          const currentClip = clipStore.clips[0];
          if (currentClip) {
            const clipStartTime = currentClip.startTime;
            const clipEndTime = currentClip.endTime;

            // Clamp time to clip bounds
            const clampedTime = Math.max(clipStartTime, Math.min(clipEndTime, time));

            // Convert to virtual time (0-based within the virtual timeline)
            const virtualTime = clampedTime - clipStartTime;

            momentMergeVideoPlayerRef.current.scrubTo(virtualTime);
          } else {
            momentMergeVideoPlayerRef.current.scrubTo(0);
          }
        }
      },
      video: momentMergeVideoPlayerRef.current?.video ?? null,
    }));

    const handleModeChange = useCallback(
      (mode: 'moment' | 'full') => {
        const oldMode = playerStore.mode;
        playerStore.setMode(mode);
        trackingStore.videoModeChanged(mode);

        // If switching from full to moment and we're outside bounds, seek to start
        if (
          oldMode === 'full' &&
          mode === 'moment' &&
          startTime !== undefined &&
          endTime !== undefined
        ) {
          const currentTime = playerStore.currentTime;
          if (currentTime < startTime || currentTime > endTime) {
            // Let the clip rebuild land before scrubbing into it.
            setTimeout(() => {
              if (momentMergeVideoPlayerRef.current) {
                momentMergeVideoPlayerRef.current.scrubTo(0); // 0 = start of moment
              }
            }, CLIP_REBUILD_DELAY_MS);
          }
        }
      },
      [startTime, endTime, playerStore],
    );

    return (
      <div
        className={classNames(css.videoContainer, containerClassName)}
        style={hidden ? { display: 'none' } : { flex: 1 }}
      >
        <div className={css.videoWrapper}>
          {/* Mode toggle overlay */}
          {withModeToggle && (
            <div className={css.toggleContainer}>
              <MultipleToggle
                className={toggleClassName}
                options={[
                  { value: 'moment', label: 'Moment' },
                  { value: 'full', label: 'Full media' },
                ]}
                value={playerStore.mode}
                onChange={handleModeChange}
              />
            </div>
          )}

          {/* Use MomentMergeVideoPlayer with our clip store */}
          <MomentMergeVideoPlayer
            ref={momentMergeVideoPlayerRef}
            onReady={() => {
              // If clip has zero duration (metadata.video.duration was missing),
              // update it from the video element's actual duration
              if (clipStore.totalClipsDuration === 0 && momentMergeVideoPlayerRef.current?.video) {
                const videoDuration = momentMergeVideoPlayerRef.current.video.duration;
                if (videoDuration && isFinite(videoDuration) && videoDuration > 0) {
                  const extMediaId = new ExtendedMediaId(media._id, media.libraryId);
                  const ts = new BeginEndTimestamp(0, videoDuration);
                  const clip = new Clip(`${media._id}-clip`, extMediaId, ts);
                  clipStore.setClips([clip]);
                }
              }
              onReady?.();
            }}
            hidden={false}
            clipStore={clipStore}
            activeMomentId={null}
            playbackSource={playbackSource}
            onPlaybackSourceChange={setPlaybackSource}
            playbackSourceLabels={playbackSourceLabels}
            onOriginalUnavailable={handleOriginalUnavailable}
            chapters={chapters}
            commentMarks={commentMarks}
            selectedCommentId={selectedCommentId}
            onCommentMarkClick={onSelectComment}
            selection={selectableRange ? selection : undefined}
            // Passing the handler is what turns the bar's drag into range-marking, so it is
            // withheld in `moment` mode — see the note on `selection`.
            onSelectionChange={rangeSelectable ? handleSelectionChange : undefined}
            onSelectionDragChange={rangeSelectable ? handleSelectionDragChange : undefined}
          />

          <VideoScanningOverlay active={showScanningEffect ?? false} />
        </div>
      </div>
    );
  }),
);
